import React, { useState, useRef, useMemo } from 'react'
import {
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Spinner,
  IconButton,
  Badge,
} from 'components/ui'
import { cn } from 'utils/cn'
import {
  Search,
  Close,
  Building,
  RecentlyViewed,
  ChevronDown,
  ChevronLeft,
  Catalog,
} from '@carbon/icons-react'
import { useOperationEnvironment } from 'contexts/OperationEnvironmentContext'
import { useProviderSelector, SelectedProvider } from 'contexts/ProviderSelectorContext'
import { useDebouncedValue } from 'hooks/useDebouncedValue'
import { useOperationProvidersQuery } from 'hooks/queries/operation'
import { OperationProvider } from 'types/operation'

type ProviderSearchResult = OperationProvider

function toSelectedProvider(
  p: ProviderSearchResult,
  environment: 'sandbox' | 'live',
): SelectedProvider {
  return {
    id: p.id,
    name: p.legalEntity?.name || p.reference || p.id,
    reference: p.reference,
    status: p.status,
    // Tag the selection with the environment it was made in (from the toggle),
    // used to scope "recent providers". Providers are env-scoped by collection.
    environment,
  }
}

function providerName(p: ProviderSearchResult): string {
  return p.legalEntity?.name || p.reference || p.id
}

function groupByLetter(items: ProviderSearchResult[]): Record<string, ProviderSearchResult[]> {
  const groups: Record<string, ProviderSearchResult[]> = {}
  for (const p of items) {
    const letter = (providerName(p)[0] || '#').toUpperCase()
    const key = /[A-Z]/.test(letter) ? letter : '#'
    if (!groups[key]) groups[key] = []
    groups[key].push(p)
  }
  return Object.keys(groups)
    .sort()
    .reduce<Record<string, ProviderSearchResult[]>>((acc, k) => {
      acc[k] = groups[k]
      return acc
    }, {})
}

interface ProviderSelectorProps {
  collapsed?: boolean
}

const ProviderSelector: React.FC<ProviderSelectorProps> = ({ collapsed }) => {
  const { environment } = useOperationEnvironment()
  const { provider, recents, setProvider, clearProvider } = useProviderSelector()
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [view, setView] = useState<'main' | 'directory'>('main')
  const [expandedLetter, setExpandedLetter] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const debouncedQuery = useDebouncedValue(query, 250)
  const trimmedQuery = debouncedQuery.trim()

  const directoryQuery = useOperationProvidersQuery({ limit: 500 })
  const searchQuery = useOperationProvidersQuery(
    { search: trimmedQuery || undefined, limit: 10 },
    { enabled: Boolean(trimmedQuery) },
  )
  const results = trimmedQuery ? (searchQuery.data ?? []) : []
  const isSearching = Boolean(trimmedQuery) && searchQuery.isFetching

  const directoryGrouped = useMemo(() => {
    const all = [...(directoryQuery.data ?? [])]
    all.sort((a, b) => providerName(a).toLowerCase().localeCompare(providerName(b).toLowerCase()))
    return groupByLetter(all)
  }, [directoryQuery.data])

  const handleSelect = (p: ProviderSearchResult) => {
    setProvider(toSelectedProvider(p, environment))
    closeAndReset()
  }

  const handleSelectRecent = (p: SelectedProvider) => {
    setProvider(p)
    closeAndReset()
  }

  const closeAndReset = () => {
    setIsOpen(false)
    setQuery('')
    setView('main')
    setExpandedLetter(null)
  }

  const envRecents = useMemo(
    () => recents.filter(r => r.environment === environment),
    [recents, environment],
  )

  const grouped = directoryGrouped
  const letters = Object.keys(grouped)

  const popoverWidth = view === 'directory' ? '420px' : '340px'

  const handleOpenAutoFocus = (e: Event) => {
    if (view === 'main' && inputRef.current) {
      e.preventDefault()
      inputRef.current.focus()
    }
  }

  const body =
    view === 'main' ? (
      <div className={cn('flex flex-col items-center', 'items-stretch gap-0')}>
        <div className={cn('flex flex-row items-center', 'border-b border-line-subtle px-3 py-2')}>
          <Search size={16} className="shrink-0 text-content-muted" />
          <Input
            ref={inputRef}
            placeholder="Search providers..."
            inputSize="sm"
            variant="unstyled"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="h-auto border-0 bg-transparent px-0 focus-visible:ring-0"
          />
          {isSearching && <Spinner size="xs" />}
        </div>

        {provider && (
          <div
            className={cn(
              'flex flex-row items-center',
              'justify-between border-b border-line-subtle bg-surface-muted px-3 py-2',
            )}
          >
            <div
              className={cn('flex flex-row items-center', 'min-w-0 flex-1 gap-2 overflow-hidden')}
            >
              <Building size={14} className="shrink-0" />
              <p className={cn('text-content-secondary', 'truncate text-xs font-semibold')}>
                {provider.name}
              </p>
            </div>
            <IconButton
              aria-label="Clear"
              icon={<Close size={14} />}
              size="xs"
              variant="ghost"
              className="shrink-0"
              onClick={e => {
                e.stopPropagation()
                clearProvider()
              }}
            />
          </div>
        )}

        <div className="max-h-[320px] overflow-y-auto">
          {query.trim() && results.length > 0 && (
            <div className={cn('flex flex-col items-center', 'items-stretch gap-0')}>
              {results.map(p => (
                <div
                  key={p.id}
                  className={cn(
                    'flex flex-row items-center',
                    'cursor-pointer gap-2 px-3 py-2 hover:bg-surface-subtle',
                  )}
                  onClick={() => handleSelect(p)}
                >
                  <Building size={14} className="shrink-0 text-content-muted" />
                  <div
                    className={cn(
                      'flex flex-col items-center',
                      'flex-1 items-start gap-0 overflow-hidden',
                    )}
                  >
                    <p className={cn('text-content-secondary', 'truncate text-xs font-medium')}>
                      {providerName(p)}
                    </p>
                    <p
                      className={cn(
                        'text-content-secondary',
                        'truncate text-[10px] text-content-muted',
                      )}
                    >
                      {p.reference} {p.businessEmail ? `· ${p.businessEmail}` : ''}
                    </p>
                  </div>
                  {p.status && (
                    <Badge
                      className="text-[9px]"
                      colorScheme={p.status === 'active' ? 'green' : 'gray'}
                    >
                      {p.status}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}

          {query.trim() && !isSearching && results.length === 0 && (
            <div className="px-3 py-4">
              <p className={cn('text-content-secondary', 'text-center text-xs text-content-muted')}>
                No providers found
              </p>
            </div>
          )}

          {!query.trim() && (
            <>
              <div
                className={cn(
                  'flex flex-row items-center',
                  'cursor-pointer gap-2 px-3 py-2 hover:bg-surface-subtle',
                )}
                onClick={() => {
                  setView('directory')
                  setExpandedLetter(null)
                }}
              >
                <Catalog size={14} className="shrink-0 text-content-muted" />
                <p
                  className={cn('text-content-secondary', 'text-xs font-medium text-content-muted')}
                >
                  A–Z Directory
                </p>
              </div>

              {envRecents.length > 0 && (
                <div className={cn('flex flex-col items-center', 'items-stretch gap-0')}>
                  <div className={cn('flex flex-row items-center', 'px-3 pb-1 pt-2')}>
                    <RecentlyViewed size={12} className="text-content-muted" />
                    <p
                      className={cn(
                        'text-content-secondary',
                        'text-[10px] font-semibold uppercase tracking-wider text-content-muted',
                      )}
                    >
                      Recent
                    </p>
                  </div>
                  {envRecents.map(r => (
                    <div
                      key={r.id}
                      className={cn(
                        'flex flex-row items-center',
                        'cursor-pointer gap-2 px-3 py-2 hover:bg-surface-subtle',
                      )}
                      onClick={() => handleSelectRecent(r)}
                    >
                      <Building size={14} className="shrink-0 text-content-muted" />
                      <p
                        className={cn(
                          'text-content-secondary',
                          'flex-1 truncate text-xs font-medium',
                        )}
                      >
                        {r.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {envRecents.length === 0 && (
                <div className="px-3 py-3">
                  <p
                    className={cn(
                      'text-content-secondary',
                      'text-center text-xs text-content-muted',
                    )}
                  >
                    Search or browse the A–Z directory
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    ) : (
      <div className={cn('flex flex-col items-center', 'items-stretch gap-0')}>
        <div className={cn('flex flex-row items-center', 'border-b border-line-subtle px-3 py-2')}>
          <ChevronLeft
            size={16}
            className="shrink-0 cursor-pointer text-content-muted hover:opacity-70"
            onClick={() => {
              setView('main')
              setExpandedLetter(null)
            }}
          />
          <p className={cn('text-content-secondary', 'flex-1 text-xs font-semibold')}>
            A–Z Directory
          </p>
        </div>

        <div className="max-h-[360px] overflow-y-auto px-3 py-2">
          {letters.length === 0 ? (
            <p
              className={cn(
                'text-content-secondary',
                'py-4 text-center text-xs text-content-muted',
              )}
            >
              No providers
            </p>
          ) : (
            <div className={cn('flex flex-col items-center', 'items-stretch gap-2')}>
              <div className={cn('flex flex-wrap items-center gap-2', 'gap-1')}>
                {letters.map(l => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setExpandedLetter(expandedLetter === l ? null : l)}
                    className={cn(
                      'min-w-[26px] cursor-pointer rounded-md px-2 py-0.5 text-center text-xs font-bold leading-relaxed hover:opacity-85',
                      expandedLetter === l ? 'bg-primary text-white' : 'bg-surface-subtle',
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>

              {expandedLetter && grouped[expandedLetter] && (
                <div className={cn('flex flex-col items-center', 'items-stretch gap-0 pt-1')}>
                  {grouped[expandedLetter].map(p => (
                    <div
                      key={p.id}
                      className={cn(
                        'flex flex-row items-center',
                        'cursor-pointer gap-2 rounded-md px-1 py-1.5 hover:bg-surface-subtle',
                      )}
                      onClick={() => handleSelect(p)}
                    >
                      <Building size={14} className="shrink-0 text-content-muted" />
                      <p
                        className={cn(
                          'text-content-secondary',
                          'flex-1 truncate text-xs font-medium',
                        )}
                      >
                        {providerName(p)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )

  if (collapsed) {
    return (
      <Popover open={isOpen} onOpenChange={o => (o ? setIsOpen(true) : closeAndReset())}>
        <PopoverTrigger asChild>
          <IconButton
            aria-label="Select provider"
            icon={<Building size={20} />}
            variant="ghost"
            size="sm"
            className={cn('w-full', provider && 'text-primary')}
          />
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          className="overflow-hidden p-0"
          style={{ width: popoverWidth, maxHeight: '420px' }}
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          {body}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={o => (o ? setIsOpen(true) : closeAndReset())}>
      <PopoverTrigger asChild>
        <div className="w-full cursor-pointer rounded-md border border-line-subtle px-3 py-2 hover:bg-surface-subtle">
          <div className={cn('flex flex-row items-center', 'justify-between gap-2')}>
            <div className={cn('flex flex-row items-center', 'flex-1 gap-2 overflow-hidden')}>
              <Building size={16} className="shrink-0 text-content-muted" />
              <p
                className={cn(
                  'text-content-secondary',
                  cn(
                    'truncate text-xs',
                    provider ? 'font-semibold' : 'font-normal text-content-muted',
                  ),
                )}
              >
                {provider ? provider.name : 'Select provider'}
              </p>
            </div>
            <ChevronDown size={14} className="shrink-0 text-content-muted" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="overflow-hidden p-0"
        style={{ width: popoverWidth, maxHeight: '420px' }}
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        {body}
      </PopoverContent>
    </Popover>
  )
}

export default ProviderSelector
